import React, { useState } from 'react';
import { TextField } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import IconSave from '@mui/icons-material/SaveAltOutlined';
import IconDelete from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../ConfirmPopup/ConfirmPopup';
import Toast from '../../components/support/Toast';
import { DataProviderInstance } from '../../api/DataProvider';
import {
  AGE_MAX_MONTHS, BAND_NOTE, SUBSCALES, TOTAL_MAX, assessmentAge, formatAge, isOutOfRange, percentileBand, totalScore,
} from '../../pages/appointment/modules/aims/aims';
import styles from './AimsPopup.module.css';

const DATE_FORMAT = 'YYYY-MM-DD';
const NOTES_MAX = 2000;
const TONE_CLASS = { alert: styles.bandAlert, info: styles.bandInfo, ok: styles.bandOk };

/** Data padrão: a do atendimento (nunca no futuro), senão hoje. */
const defaultDate = (appointmentDate) => {
  const today = dayjs().format(DATE_FORMAT);
  if (!appointmentDate || !dayjs(appointmentDate).isValid()) return today;
  const date = dayjs(appointmentDate).format(DATE_FORMAT);
  return date > today ? today : date;
};

const digitsOnly = (text, maxLength) => text.replace(/\D/g, '').slice(0, maxLength);
const isInRange = (text, max) => text !== '' && Number(text) >= 0 && Number(text) <= max;

/**
 * Registro ou edição da avaliação AIMS do atendimento: data, escore de cada
 * postura (com o máximo ao lado), percentil lido no gráfico oficial e
 * observações. Total, idade e faixa aparecem ao vivo.
 */
function AimsPopup({ patientId, appointmentId, appointmentDate, birthday, birthInfo, entry, OnHide }) {
  const { SetPatientAimsAssessment, DeletePatientAimsAssessment } = DataProviderInstance();

  const [date, setDate] = useState(entry ? dayjs(entry.assessedAt).format(DATE_FORMAT) : defaultDate(appointmentDate));
  const [scores, setScores] = useState(() => Object.fromEntries(
    SUBSCALES.map((item) => [item.key, entry && entry[item.key] != null ? String(entry[item.key]) : ''])
  ));
  const [percentile, setPercentile] = useState(entry && entry.percentile != null ? String(entry.percentile) : '');
  const [notes, setNotes] = useState((entry && entry.notes) || '');
  const [saving, setSaving] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });

  const showToast = (message, severity = 'error') => setToast({ open: true, message, severity });

  const allValid = SUBSCALES.every((item) => isInRange(scores[item.key], item.max));
  const total = allValid ? totalScore(scores) : null;
  const percentileValid = percentile === '' || isInRange(percentile, 100);
  const band = percentileValid ? percentileBand(percentile) : null;
  const age = date ? assessmentAge({ assessedAt: date }, birthday, birthInfo) : null;

  function saveHandler() {
    if (saving) return;
    if (!date) {
      showToast('Informe a data da avaliação.');
      return;
    }
    const invalid = SUBSCALES.find((item) => !isInRange(scores[item.key], item.max));
    if (invalid) {
      showToast(`Informe o escore de ${invalid.label.toLowerCase()}, de 0 a ${invalid.max}.`);
      return;
    }
    if (!percentileValid) {
      showToast('O percentil deve ficar entre 0 e 100.');
      return;
    }

    setSaving(true);
    const payload = {
      assessedAt: date,
      ...Object.fromEntries(SUBSCALES.map((item) => [item.key, Number(scores[item.key])])),
      percentile: percentile === '' ? '' : Number(percentile),
      notes: notes.trim(),
    };
    SetPatientAimsAssessment(patientId, entry ? entry.appointmentId : appointmentId, payload, (response) => {
      setSaving(false);
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast(response?.error || 'Não foi possível salvar a avaliação.');
      }
    });
  }

  function deleteHandler() {
    DeletePatientAimsAssessment(entry.id, (response) => {
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast('Não foi possível excluir a avaliação.');
      }
    });
  }

  const content = (
    <div className={styles.container}>
      <div className={styles.header}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Data da avaliação"
            value={date ? dayjs(date) : null}
            format="DD/MM/YYYY"
            disableFuture={true}
            onChange={(value) => setDate(value && value.isValid() ? value.format(DATE_FORMAT) : null)}
            slotProps={{ textField: { size: 'small' } }}
          />
        </LocalizationProvider>
        {age && <span className={styles.age}>Idade na avaliação: {formatAge(age)}</span>}
      </div>

      {isOutOfRange(age) && (
        <span className={styles.warning}>
          A escala foi feita para bebês de 0 a {AGE_MAX_MONTHS} meses. Você pode registrar, mas interprete com cautela.
        </span>
      )}

      <div>
        <span className={styles.sectionLabel}>Escore por postura</span>
        <div className={styles.scores}>
          {SUBSCALES.map((item) => {
            const text = scores[item.key];
            return (
              <TextField
                key={item.key}
                size="small"
                label={item.label}
                value={text}
                onChange={(e) => setScores((current) => ({ ...current, [item.key]: digitsOnly(e.target.value, 2) }))}
                error={text !== '' && !isInRange(text, item.max)}
                helperText={`de 0 a ${item.max}`}
                inputProps={{ inputMode: 'numeric' }}
              />
            );
          })}
        </div>
      </div>

      <div className={styles.result}>
        <div className={styles.total}>
          <span className={styles.totalValue}>{total != null ? total : '-'}</span>
          <span className={styles.totalMax}>/ {TOTAL_MAX}</span>
          <span className={styles.totalLabel}>Total</span>
        </div>
        <TextField
          size="small"
          label="Percentil (opcional)"
          value={percentile}
          onChange={(e) => setPercentile(digitsOnly(e.target.value, 3))}
          error={!percentileValid}
          helperText="Lido no gráfico oficial da escala"
          inputProps={{ inputMode: 'numeric' }}
          className={styles.percentileField}
        />
        {band && <span className={`${styles.band} ${TONE_CLASS[band.tone]}`}>{band.label}</span>}
      </div>

      <span className={styles.hint}>
        Aplique a escala com o material oficial e registre aqui o escore de cada postura. {BAND_NOTE}
      </span>

      <TextField
        size="small"
        label="Observações (opcional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX))}
        multiline
        minRows={2}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      {entry && (
        <CustomButton
          variant="contained"
          style="error"
          label="Excluir"
          icon={<IconDelete />}
          onClick={() => setShowConfirmDelete(true)}
          enabled={!saving}
        />
      )}

      <CustomButton
        variant="outlined"
        style="secondary"
        label="Cancelar"
        onClick={() => OnHide(false)}
        enabled={!saving}
      />

      <CustomButton
        style="primary"
        variant="contained"
        label={entry ? 'Atualizar' : 'Registrar'}
        icon={<IconSave />}
        onClick={saveHandler}
        loading={saving}
        enabled={!saving}
      />

      {showConfirmDelete && (
        <ConfirmPopup
          Visibility={showConfirmDelete}
          Title="Confirmar exclusão"
          Description="Tem certeza que deseja excluir esta avaliação pela escala de Alberta?"
          ConfirmLabel="Excluir"
          OnConfirm={deleteHandler}
          OnHide={() => setShowConfirmDelete(false)}
        />
      )}
    </div>
  );

  return (
    <PopupBase
      Title={entry ? 'Editar avaliação AIMS' : 'Nova avaliação AIMS'}
      Content={content}
      Footer={footer}
      OnClose={() => OnHide(false)}
    />
  );
}

export default AimsPopup;
